import React, { useEffect, useState, useCallback } from "react";
import Logger from "./Logger";
import { getLoggerBaseURL } from "../utils/getLoggerBaseURL";

// Initialize Logger
const logger = new Logger(getLoggerBaseURL());

const CustomerDataList = () => {
    const [customers, setCustomers] = useState([]); // current page items
    const [page, setPage] = useState(1);
    const [limit] = useState(10); // server page size
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]); // selections
    const [actionLoading, setActionLoading] = useState(false); // action in-progress flags
    const [exporting, setExporting] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedCustomerId, setSelectedCustomerId] = useState(null);
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [notesOpen, setNotesOpen] = useState(false);
    const [notesText, setNotesText] = useState("");
    const [notesCustomerId, setNotesCustomerId] = useState(null);

    // helper to fetch page from server
    const fetchPage = useCallback(async (pageToLoad = 1, searchTerm = "") => {
        try {
            setLoading(true);
            const url = `/api/customers/all?page=${pageToLoad}&limit=${limit}&search=${encodeURIComponent(searchTerm)}&status=${statusFilter}`;
            const res = await fetch(url);
            if (!res.ok) throw new Error("Failed to fetch customers");
            const data = await res.json();
            setCustomers(data.customers || []);
            setPage(data.page || pageToLoad);
            setTotalPages(data.totalPages || 1);
            setTotalItems(data.total || (data.customers ? data.customers.length : 0));
            setSelectedIds([]);
        } catch (err) {
            logger.log(`Fetch error: ${err}`, "ERROR");
        } finally {
            setLoading(false);
        }
    }, [limit, statusFilter]);

    // initial + whenever page or search changes
    useEffect(() => {
        fetchPage(page, search);
    }, [fetchPage, page, search]);

    useEffect(() => {
        setPage(1);
        fetchPage(1, search);
    }, [statusFilter, fetchPage, search]);

    // toggle select single row
    const toggleSelect = (id) => {
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    // select/deselect all on current page
    const toggleSelectAllOnPage = () => {
        const idsOnPage = customers.map((c) => c._id);
        const allSelected = idsOnPage.every((id) => selectedIds.includes(id));
        if (allSelected) {
            setSelectedIds((prev) => prev.filter((id) => !idsOnPage.includes(id)));
        } else {
            setSelectedIds((prev) => Array.from(new Set([...prev, ...idsOnPage])));
        }
    };

    // delete single
    const deleteCustomer = async (id) => {
        if (!window.confirm("Are you sure you want to delete this customer?")) return;
        try {
            setActionLoading(true);
            const res = await fetch(`/api/customers/delete/${id}`, { method: "DELETE" });
            if (!res.ok) throw new Error("Delete failed");
            await fetchPage(page, search);
        } catch (err) {
            logger.log(`Another error: ${err}`, "ERROR");
            alert("Failed to delete");
        } finally {
            setActionLoading(false);
        }
    };

    const handleThreeDotsClick = (event, id) => {
        if (anchorEl === event.currentTarget) {
            setAnchorEl(null);
        } else {
            setAnchorEl(event.currentTarget);
            setSelectedCustomerId(id);
        }
    };

    // export all (via blob) - downloads whole DB export endpoint
    const exportExcel = async () => {
        try {
            setExporting(true);
            const res = await fetch("/api/customers/export-excel");
            if (!res.ok) throw new Error("Export failed");
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "customers.xlsx";
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            logger.log(`Found error: ${err}`, "ERROR");
            alert("Export failed");
        } finally {
            setExporting(false);
        }
    };

    // send single email (prompts subject + body)
    const sendEmailTo = async (id) => {
        try {
            setActionLoading(true);
            await fetch(`/api/customers/send-email/${id}`, {
                method: "POST"
            });
            await fetchPage(page, search);
            alert("Email sent");
        } catch (err) {
            alert("Failed to send email", err);
        } finally {
            setActionLoading(false);
        }
    };

    // send single WhatsApp
    const sendWhatsAppTo = async (id) => {
        try {
            setActionLoading(true);

            const res = await fetch(`/api/customers/send-whatsapp/${id}`, {
                method: "POST",
            });

            if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(errorData.message || "WhatsApp sending failed");
            }

            await fetchPage(page, search);
            alert("WhatsApp sent successfully");

        } catch (error) {
            logger.log(`WhatsApp Error: ${error}`, "ERROR");
            alert(error.message || "Failed to send WhatsApp");
        } finally {
            setActionLoading(false);
        }
    };

    // Bulk email (selectedIds)
    const sendBulkEmail = async () => {
        if (!selectedIds.length) {
            alert("Select at least one customer");
            return;
        }

        // Convert selected IDs to customer objects
        const customersToSend = selectedIds.map(id => {
            const c = customers.find(cust => cust._id === id);
            return {
                _id: c._id,
                customer_name: c.customer_name,
                customer_email: c.customer_email
            };
        });

        try {
            setActionLoading(true);

            const res = await fetch(`/api/customers/send-email-bulk`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ customers: customersToSend })
            });

            const data = await res.json();

            if (!res.ok) {
                alert(data.message || "Failed to send bulk email");
                return;
            }

            alert("Bulk Email sent successfully");
            await fetchPage(page, search);

        } catch (err) {
            alert(`Failed: ${err.message}`);
        } finally {
            setActionLoading(false);
        }
    };

    // Bulk WhatsApp
    const sendBulkWhatsApp = async () => {
        if (!selectedIds.length) return alert("Select at least one customer");

        try {
            setActionLoading(true);

            const res = await fetch(`/api/customers/send-whatsapp-bulk`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ids: selectedIds })
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data?.message || "Bulk WhatsApp failed");

            await fetchPage(page, search);
            alert("Bulk WhatsApp Sent!");
        } catch (err) {
            logger.log(`Error: ${err}`, "ERROR");
            alert("Bulk WhatsApp failed");
        } finally {
            setActionLoading(false);
        }
    };

    // Delete all customers
    const deleteAllCustomers = async () => {
        if (!window.confirm("Are you sure you want to delete all customers?")) return;
        try {
            setActionLoading(true);
            const res = await fetch(`/api/customers/deleteAll`, { method: "DELETE" });
            if (!res.ok) throw new Error("Delete failed");
            await fetchPage(page, search);
        } catch (err) {
            logger.log(`Error: ${err}`, "ERROR");
            alert("Failed to delete");
        } finally {
            setActionLoading(false);
        }
    }

    // UI helpers
    const isSelected = (id) => selectedIds.includes(id);
    const allOnPageSelected = customers.length && customers.every((c) => selectedIds.includes(c._id));

    const saveNotes = async () => {
        if (!notesText.trim()) {
            alert("Notes cannot be empty");
            return;
        }

        try {
            setActionLoading(true);

            const res = await fetch(`/api/customers/add-notes/${notesCustomerId}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ notes: notesText })
            });

            if (!res.ok) throw new Error("Failed to save notes");

            setNotesOpen(false);
            setNotesText("");
            setNotesCustomerId(null);

            await fetchPage(page, search);
            alert("Notes saved");

        } catch (err) {
            logger.log(`Error: ${err}`, "ERROR");
            alert("Failed to save notes " + err.message);
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-100 p-5 flex justify-center">
            <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', backgroundColor: '#fff', zIndex: 1000, boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)', padding: '0px 0px', height: '60px', }} >
                <div className="w-full" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div className="title" onClick={() => (window.location.href = '/Marketing')} style={{ alignItems: 'left', marginLeft: '20px', paddingTop: '10px', cursor: 'pointer', display: 'flex' }} >
                        <img src="https://zordigo.com/assets/Logo2.png" className="w-10 h-auto" alt="Zordigo Logo" />
                        <b style={{ color: "#c1272c" }}>Zordigo</b>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'right', justifyContent: 'space-between', gap: '2px', marginRight: '20px' }}>
                        <div className="link-content" style={{ display: 'flex', alignItems: 'right', marginRight: '40px', cursor: 'pointer' }} title="Home Page" onClick={() => (window.location.href = '/Marketing')}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" class="bi bi-arrow-left-square" viewBox="0 0 16 16" color="#c1272c" >
                                <path stroke="currentColor" strokeWidth="0.4" d="M8.354 1.146a.5.5 0 0 0-.708 0l-6 6A.5.5 0 0 0 1.5 7.5v7a.5.5 0 0 0 .5.5h4.5a.5.5 0 0 0 .5-.5v-4h2v4a.5.5 0 0 0 .5.5H14a.5.5 0 0 0 .5-.5v-7a.5.5 0 0 0-.146-.354L13 5.793V2.5a.5.5 0 0 0-.5-.5h-1a.5.5 0 0 0-.5.5v1.293zM2.5 14V7.707l5.5-5.5 5.5 5.5V14H10v-4a.5.5 0 0 0-.5-.5h-3a.5.5 0 0 0-.5.5v4z" />
                            </svg>
                        </div>
                        <div className="link-content" style={{ display: 'flex', alignItems: 'right', marginRight: '40px', cursor: 'pointer' }} title="Log Out" onClick={() => (window.location.href = '/')} >
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" class="bi bi-box-arrow-right" viewBox="0 0 16 16" color="#c1272c" >
                                <path stroke="currentColor" strokeWidth="0.6" fill-rule="evenodd" d="M10 12.5a.5.5 0 0 1-.5.5h-8a.5.5 0 0 1-.5-.5v-9a.5.5 0 0 1 .5-.5h8a.5.5 0 0 1 .5.5v2a.5.5 0 0 0 1 0v-2A1.5 1.5 0 0 0 9.5 2h-8A1.5 1.5 0 0 0 0 3.5v9A1.5 1.5 0 0 0 1.5 14h8a1.5 1.5 0 0 0 1.5-1.5v-2a.5.5 0 0 0-1 0z" />
                                <path stroke="currentColor" strokeWidth="0.6" fill-rule="evenodd" d="M15.854 8.354a.5.5 0 0 0 0-.708l-3-3a.5.5 0 0 0-.708.708L14.293 7.5H5.5a.5.5 0 0 0 0 1h8.793l-2.147 2.146a.5.5 0 0 0 .708.708z" />
                            </svg>
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-white shadow-xl rounded-lg p-6 w-full max-w-auto" style={{ marginTop: '60px' }}>

                {/* Header */}
                <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Uploaded Customer Data</h1>
                        <p className="text-sm text-gray-500 mt-1">Total Records : {totalItems}</p>
                    </div>

                    <div className="flex gap-2 items-center">
                        <input
                            type="text"
                            placeholder="Search by name / email / phone"
                            className="border px-4 py-2 rounded-lg w-60 focus:ring-2 focus:ring-blue-500"
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(1); // reset to first page on search
                            }}
                        />
                        <select
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter(e.target.value);
                                setPage(1);
                            }}
                            className="border px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="ALL">All</option>
                            <option value="WA_SENT">WhatsApp Sent</option>
                            <option value="WA_NOT_SENT">WhatsApp Not Sent</option>
                            <option value="MAIL_SENT">Email Sent</option>
                            <option value="MAIL_NOT_SENT">Email Not Sent</option>
                            <option value="NONE_SENT">None Sent</option>
                        </select>
                        <button onClick={() => fetchPage(1, search)} className="px-3 py-2 rounded-md border hover:bg-gray-50 flex items-center justify-center" disabled={loading} title="Refresh"  >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-arrow-clockwise" viewBox="0 0 16 16">
                                <path stroke="currentColor" stroke-width="0.5" fill="currentColor" fill-rule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2z" />
                                <path stroke="currentColor" stroke-width="0.5" fill="currentColor" d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466" />
                            </svg>
                        </button>

                        <button onClick={exportExcel} className="bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 flex items-center justify-center transition" disabled={exporting} title="Export Excel" >
                            {exporting ? (
                                <span className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></span>
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-download" viewBox="0 0 16 16">
                                    <path stroke="currentColor" stroke-width="0.7" fill="currentColor" d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" />
                                    <path stroke="currentColor" stroke-width="0.7" fill="currentColor" d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z" />
                                </svg>
                            )}
                        </button>

                        <div className="flex gap-2">
                            {/* Send Email */}
                            {selectedIds.length > 0 && (
                                <button
                                    onClick={sendBulkEmail}
                                    className={`bg-blue-600 text-white p-2 rounded-md flex items-center justify-center
                                        ${actionLoading
                                            ? "cursor-not-allowed opacity-40"
                                            : "hover:bg-blue-700 cursor-pointer"
                                        }`}
                                    disabled={actionLoading}
                                    title="Send Email to Selected"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                        fill="currentColor" className="bi bi-envelope-arrow-up"
                                        viewBox="0 0 16 16">
                                        <path stroke="currentColor" strokeWidth="0.4" fill="currentColor"
                                            d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v4.5a.5.5 0 0 1-1 0V5.383l-7 4.2-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h5.5a.5.5 0 0 1 0 1H2a2 2 0 0 1-2-1.99zm1 7.105 4.708-2.897L1 5.383zM1 4v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1" />
                                        <path d="M12.5 16a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7m.354-5.354 1.25 1.25a.5.5 0 0 1-.708.708L13 12.207V14a.5.5 0 0 1-1 0v-1.717l-.28.305a.5.5 0 0 1-.737-.676l1.149-1.25a.5.5 0 0 1 .722-.016" />
                                    </svg>
                                </button>
                            )}

                            {/* Send WhatsApp */}
                            {selectedIds.length > 0 && (
                                <button
                                    onClick={sendBulkWhatsApp}
                                    className={`bg-blue-600 text-white p-2 rounded-md flex items-center justify-center
                                        ${actionLoading
                                            ? "cursor-not-allowed opacity-40"
                                            : "hover:bg-blue-700 cursor-pointer"
                                        }`}
                                    disabled={actionLoading}
                                    title="Send WhatsApp to Selected"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                        fill="currentColor" className="bi bi-whatsapp" viewBox="0 0 16 16">
                                        <path d="M13.601 2.326A7.85 7.85 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.9 7.9 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.9 7.9 0 0 0 13.6 2.326zM7.994 14.521a6.6 6.6 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.56 6.56 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592m3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.73.73 0 0 0-.529.247c-.182.198-.691.677-.691 1.654s.71 1.916.81 2.049c.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232" />
                                    </svg>
                                </button>
                            )}

                            {/* Delete All */}
                            <button onClick={deleteAllCustomers} className="bg-red-600 text-white p-2 rounded-md hover:bg-red-700 flex items-center justify-center" disabled={actionLoading} title="Delete All Customers" >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-trash3" viewBox="0 0 16 16">
                                    <path stroke="currentColor" stroke-width="0.4" fill="currentColor" d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5M11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66h.538a.5.5 0 0 0 0-1zm1.958 1-.846 10.58a1 1 0 0 1-.997.92h-6.23a1 1 0 0 1-.997-.92L3.042 3.5zm-7.487 1a.5.5 0 0 1 .528.47l.5 8.5a.5.5 0 0 1-.998.06L5 5.03a.5.5 0 0 1 .47-.53Zm5.058 0a.5.5 0 0 1 .47.53l-.5 8.5a.5.5 0 1 1-.998-.06l.5-8.5a.5.5 0 0 1 .528-.47M8 4.5a.5.5 0 0 1 .5.5v8.5a.5.5 0 0 1-1 0V5a.5.5 0 0 1 .5-.5" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Loading */}
                {loading && (
                    <p className="text-center text-lg text-gray-600">Loading...</p>
                )}

                {/* Table */}
                {!loading && customers.length === 0 && (
                    <p className="text-center text-lg text-gray-600">No customer data found.</p>
                )}

                {!loading && customers.length > 0 && (
                    <div className="overflow-x-auto mt-4">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-blue-600 hover:bg-blue-600 text-white">
                                    <th className="py-3 px-4">
                                        <input
                                            type="checkbox"
                                            checked={allOnPageSelected}
                                            onChange={toggleSelectAllOnPage}
                                        />
                                    </th>
                                    <th className="py-3 px-4">Name</th>
                                    <th className="py-3 px-4">Email</th>
                                    <th className="py-3 px-4">Phone</th>
                                    <th className="py-3 px-4">Uploaded</th>
                                    <th className="py-3 px-4">Email Sent</th>
                                    <th className="py-3 px-4">WhatsApp Sent</th>
                                    <th className="py-3 px-4 text-center">Notes</th>
                                    <th className="py-3 px-4 text-center">Actions</th>
                                </tr>
                            </thead>

                            <tbody>
                                {customers.map((item, index) => (
                                    <tr key={item._id} className="border-b hover:bg-gray-50">
                                        <td className="py-3 px-4">
                                            <input
                                                type="checkbox"
                                                checked={isSelected(item._id)}
                                                onChange={() => toggleSelect(item._id)}
                                            />
                                        </td>
                                        <td className="py-3 px-4">{item.customer_name ? item.customer_name : "-"}</td>
                                        <td className="py-3 px-4">{item.customer_email ? item.customer_email : "-"}</td>
                                        <td className="py-3 px-4">{item.customer_phone ? item.customer_phone : "-"}</td>
                                        <td className="py-3 px-4">{item.createdAt ? new Date(item.createdAt).toLocaleString() : "-"}</td>
                                        <td className="py-3 px-4">
                                            {item.has_received_email ? (
                                                <div className="text-sm text-green-700">
                                                    Yes
                                                    <div className="text-sm text-green-700">
                                                        <span className="text-xs text-gray-500">{item.email_sent_at ? new Date(item.email_sent_at).toLocaleString() : ""}</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="text-sm text-gray-500">No</div>
                                            )}
                                        </td>
                                        <td className="py-3 px-4">
                                            {item.has_received_whatsapp ? (
                                                <div className="text-sm text-green-700">
                                                    Yes
                                                    <div className="text-sm text-green-700">
                                                        <span className="text-xs text-gray-500">{item.whatsapp_sent_at ? new Date(item.whatsapp_sent_at).toLocaleString() : ""}</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="text-sm text-gray-500">No</div>
                                            )}
                                        </td>
                                        <td className="py-3 px-4">{item.notes ? item.notes : "-"}</td>
                                        <td className="py-3 px-4 text-center">
                                            <button
                                                onClick={(e) => handleThreeDotsClick(e, item._id)}
                                                style={{
                                                    padding: "4px 10px",
                                                    cursor: "pointer",
                                                    fontSize: "18px",
                                                    lineHeight: "12px"
                                                }}
                                            >
                                                ...
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <CustomerActionsDropdown
                    anchorEl={anchorEl}
                    onClose={() => setAnchorEl(null)}
                    onSendEmail={() => {
                        sendEmailTo(selectedCustomerId);
                        setAnchorEl(null);
                    }}
                    onSendWA={() => {
                        sendWhatsAppTo(selectedCustomerId);
                        setAnchorEl(null);
                    }}
                    addNotes={() => {
                        setNotesCustomerId(selectedCustomerId);
                        setNotesText("");
                        setNotesOpen(true);
                        setAnchorEl(null);
                    }}
                    onDelete={() => {
                        deleteCustomer(selectedCustomerId);
                        setAnchorEl(null);
                    }}
                />

                {/* Pagination */}
                <div className="flex justify-center mt-6 gap-2">
                    <button
                        disabled={page === 1 || loading}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        className={`px-4 py-2 rounded-lg border ${page === 1 ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-200"}`}
                    >
                        Previous
                    </button>

                    {Array.from({ length: totalPages }).map((_, i) => (
                        <button
                            key={i}
                            onClick={() => setPage(i + 1)}
                            className={`px-4 py-2 rounded-lg border ${page === i + 1 ? "bg-blue-600 text-white" : "hover:bg-gray-200"}`}
                        >
                            {i + 1}
                        </button>
                    ))}

                    <button
                        disabled={page === totalPages || loading}
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        className={`px-4 py-2 rounded-lg border ${page === totalPages ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-200"}`}
                    >
                        Next
                    </button>
                </div>
            </div>

            {notesOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[3000]">
                    <div className="bg-white rounded-lg shadow-xl w-[400px] p-5">
                        <h2 className="text-lg font-semibold mb-3">Add Notes</h2>

                        <textarea
                            value={notesText}
                            onChange={(e) => setNotesText(e.target.value)}
                            rows={4}
                            placeholder="Enter notes for this customer..."
                            className="w-full border rounded-md p-2 focus:ring-2 focus:ring-blue-500"
                        />

                        <div className="flex justify-end gap-2 mt-4">
                            <button
                                onClick={() => setNotesOpen(false)}
                                className="px-4 py-2 border rounded-md hover:bg-gray-100"
                            >
                                Cancel
                            </button>

                            <button
                                onClick={saveNotes}
                                disabled={actionLoading}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomerDataList;

const CustomerActionsDropdown = ({
    anchorEl,
    onClose,
    onSendEmail,
    onSendWA,
    addNotes,
    onDelete
}) => {
    if (!anchorEl) return null;

    const rect = anchorEl.getBoundingClientRect();

    return (
        <div
            style={{
                position: "fixed",
                top: rect.bottom + 6,
                left: rect.left - 74,
                background: "white",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                padding: "6px 0",
                zIndex: 2000,
                width: "160px"
            }}
        >
            <button
                onClick={onSendEmail}
                style={{
                    width: "100%",
                    background: "none",
                    border: "none",
                    padding: "8px 12px",
                    textAlign: "left",
                    cursor: "pointer",
                    fontSize: "14px",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px"
                }}
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-envelope" viewBox="0 0 16 16">
                    <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1zm13 2.383-4.708 2.825L15 11.105zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741M1 11.105l4.708-2.897L1 5.383z" />
                </svg>
                Send Email
            </button>

            <button
                onClick={onSendWA}
                style={{
                    width: "100%",
                    background: "none",
                    border: "none",
                    padding: "8px 12px",
                    textAlign: "left",
                    cursor: "pointer",
                    fontSize: "14px",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px"
                }}
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-whatsapp" viewBox="0 0 16 16">
                    <path d="M13.601 2.326A7.85 7.85 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.9 7.9 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.9 7.9 0 0 0 13.6 2.326zM7.994 14.521a6.6 6.6 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.56 6.56 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592m3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.73.73 0 0 0-.529.247c-.182.198-.691.677-.691 1.654s.71 1.916.81 2.049c.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232" />
                </svg>
                Send WhatsApp
            </button>

            <button
                onClick={addNotes}
                style={{
                    width: "100%",
                    background: "none",
                    border: "none",
                    padding: "8px 12px",
                    textAlign: "left",
                    cursor: "pointer",
                    fontSize: "14px",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px"
                }}
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-whatsapp" viewBox="0 0 16 16">
                    <path fill-rule="evenodd" d="M6 8V1h1v6.117L8.743 6.07a.5.5 0 0 1 .514 0L11 7.117V1h1v7a.5.5 0 0 1-.757.429L9 7.083 6.757 8.43A.5.5 0 0 1 6 8" />
                    <path d="M3 0h10a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2v-1h1v1a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v1H1V2a2 2 0 0 1 2-2" />
                    <path d="M1 5v-.5a.5.5 0 0 1 1 0V5h.5a.5.5 0 0 1 0 1h-2a.5.5 0 0 1 0-1zm0 3v-.5a.5.5 0 0 1 1 0V8h.5a.5.5 0 0 1 0 1h-2a.5.5 0 0 1 0-1zm0 3v-.5a.5.5 0 0 1 1 0v.5h.5a.5.5 0 0 1 0 1h-2a.5.5 0 0 1 0-1z" />
                </svg>
                Notes
            </button>

            <button
                onClick={onDelete}
                style={{
                    width: "100%",
                    background: "none",
                    border: "none",
                    padding: "8px 12px",
                    textAlign: "left",
                    cursor: "pointer",
                    fontSize: "14px",
                    color: "#dc2626",
                    fontWeight: "500",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px"
                }}
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-trash" viewBox="0 0 16 16">
                    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0z" />
                    <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4zM2.5 3h11V2h-11z" />
                </svg>
                Delete
            </button>

            <button
                onClick={onClose}
                style={{
                    width: "100%",
                    background: "none",
                    borderTop: "1px solid #e5e7eb",
                    padding: "8px 12px",
                    textAlign: "left",
                    cursor: "pointer",
                    fontSize: "14px",
                    marginTop: "4px",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px"
                }}
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-x" viewBox="0 0 16 16">
                    <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708" />
                </svg>
                Close
            </button>
        </div>
    );
};